// utils/FormatDateGlobal.js

/**
 * Globally used date formatter
 * Examples:
 *  - "2025-11-16T18:20:00Z" -> "16 Nov 2025, 18:20 PM"
 *  - "16.11.2025 18:20"     -> "16 Nov 2025, 18:20 PM"
 */
export const formatDateGlobal = (raw) => {
  if (!raw) return "N/A";

  const dateString = String(raw).trim();

  let timeZone = "UTC";
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    // fallback to UTC
  }

  // Match "dd.mm.yyyy" or "dd.mm.yyyy HH:MM"
  const dotDateRE =
    /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/;

  // Do we already have explicit time in the raw string?
  const hasExplicitTime =
    /T\d{2}:\d{2}/.test(dateString) || /\s\d{1,2}:\d{2}/.test(dateString);

  let d;
  const m = dateString.match(dotDateRE);

  if (m) {
    const [, dd, mm, yyyy, HH, MM] = m;
    d = new Date(
      parseInt(yyyy, 10),
      parseInt(mm, 10) - 1,
      parseInt(dd, 10),
      HH ? parseInt(HH, 10) : 0,
      MM ? parseInt(MM, 10) : 0,
      0,
      0
    );
  } else {
    d = new Date(dateString);
  }

  if (isNaN(d.getTime())) return raw;

  // Date part: "16 Nov 2025"
  const datePart = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone,
  }).format(d);

  // If no explicit time in original value, just return the date
  if (!hasExplicitTime) return datePart;

  // Time part in 24h format but with AM/PM suffix
  const timeFormatter = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false, // 24h: "18:20"
    timeZone,
  });

  const parts = timeFormatter.formatToParts(d);
  const hour = parts.find((p) => p.type === "hour")?.value ?? "00";
  const minute = parts.find((p) => p.type === "minute")?.value ?? "00";

  const hourNum = parseInt(hour, 10);
  const suffix = hourNum < 12 ? "AM" : "PM";

  const timePart = `${hour}:${minute} ${suffix}`;

  return `${datePart}, ${timePart}`;
};

// Optional default export if you like to import without braces
export default formatDateGlobal;
